import React, { createContext, useContext, ReactNode, RefObject, useCallback, useMemo, useState, useRef } from 'react';

export type SectionId = 'header' | 'search' | 'features' | 'about' | 'faq';

interface IScrollContext {
    scrollToSection: (sectionId: SectionId, options?: ScrollIntoViewOptions) => void;
    visibleSection: string;
    setVisibleSection: (sectionId: string) => void;
    registerRef: (sectionId: SectionId, ref: RefObject<any>) => void;
}

const ScrollContext = createContext<IScrollContext | undefined>(undefined);

export const ScrollProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const refs = useRef<Record<string, RefObject<any> | null>>({});
    const [visibleSection, setVisibleSectionState] = useState<string>('search');
    const isManualScrolling = useRef(false);
    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

    const registerRef = useCallback((sectionId: SectionId, ref: RefObject<any>) => {
        refs.current[sectionId] = ref;
    }, []);

    const setVisibleSection = useCallback((sectionId: string) => {
        if (!isManualScrolling.current) {
            setVisibleSectionState(sectionId);
        }
    }, []);

    const scrollToSection = useCallback((sectionId: SectionId, options: ScrollIntoViewOptions = { behavior: 'smooth' }) => {
        const targetRef = refs.current[sectionId];
        if (targetRef?.current) {
            // Lock manual scroll tracking to prevent underline jumping
            isManualScrolling.current = true;
            setVisibleSectionState(sectionId);

            targetRef.current.scrollIntoView(options);

            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
            scrollTimeout.current = setTimeout(() => {
                isManualScrolling.current = false;
            }, 800); // 800ms is the typical duration of Next.js smooth scroll
        }
    }, []);

    const value = useMemo(() => ({
        scrollToSection,
        visibleSection,
        setVisibleSection,
        registerRef
    }), [scrollToSection, visibleSection, registerRef, setVisibleSection]);

    return (
        <ScrollContext.Provider value={value}>
            {children}
        </ScrollContext.Provider>
    );
};

export const useScroll = (): IScrollContext => {
    const context = useContext(ScrollContext);
    if (context === undefined) {
        throw new Error('useScroll must be used within a ScrollProvider');
    }
    return context;
};